import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  IconButton,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Delete,
  ContentCopy,
  Check,
  Webhook as WebhookIcon,
  VpnKey,
  Send,
  History,
  ExpandMore,
  Code,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiTokenService, webhookService } from '../services/api';

// URL de base de l'API publique (ERA_API_URL côté développeur tiers)
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api` : 'https://app-era.groupeoctal.com/api');

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? 'Copié !' : 'Copier'}>
      <IconButton
        size="small"
        onClick={() => {
          navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function DeveloperPage() {
  const navigate = useNavigate();

  // ── Tokens ─────────────────────────────────────────────
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [tokenDialog, setTokenDialog] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [creatingToken, setCreatingToken] = useState(false);
  const [newTokenValue, setNewTokenValue] = useState(null);

  // ── Webhooks ───────────────────────────────────────────
  const [webhooks, setWebhooks] = useState([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(true);
  const [availableEvents, setAvailableEvents] = useState([]);
  const [whDialog, setWhDialog] = useState(false);
  const [whUrl, setWhUrl] = useState('');
  const [whEvents, setWhEvents] = useState([]);
  const [whDescription, setWhDescription] = useState('');
  const [savingWh, setSavingWh] = useState(false);
  const [newWebhookSecret, setNewWebhookSecret] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [error, setError] = useState('');

  // ── Deliveries ─────────────────────────────────────────
  const [deliveriesDialog, setDeliveriesDialog] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  useEffect(() => {
    apiTokenService.list()
      .then((res) => setTokens(res.data.tokens))
      .catch(() => {})
      .finally(() => setLoadingTokens(false));

    webhookService.list()
      .then((res) => setWebhooks(res.data.webhooks))
      .catch(() => {})
      .finally(() => setLoadingWebhooks(false));

    webhookService.listEvents()
      .then((res) => setAvailableEvents(res.data.events))
      .catch(() => {});
  }, []);

  // ── Handlers Tokens ────────────────────────────────────
  const handleCreateToken = async () => {
    if (!tokenName.trim()) return;
    setCreatingToken(true);
    try {
      const res = await apiTokenService.create({ name: tokenName.trim() });
      setNewTokenValue(res.data.token);
      setTokens((prev) => [res.data.record, ...prev]);
      setTokenName('');
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la création du token');
    } finally {
      setCreatingToken(false);
    }
  };

  const handleRevokeToken = async (id) => {
    if (!window.confirm('Révoquer ce token ? Les intégrations qui l\'utilisent cesseront de fonctionner.')) return;
    try {
      await apiTokenService.revoke(id);
      setTokens((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la révocation');
    }
  };

  const closeTokenDialog = () => {
    setTokenDialog(false);
    setNewTokenValue(null);
    setTokenName('');
  };

  // ── Handlers Webhooks ──────────────────────────────────
  const openWhDialog = () => {
    setWhUrl('');
    setWhEvents([]);
    setWhDescription('');
    setNewWebhookSecret(null);
    setWhDialog(true);
  };

  const handleCreateWebhook = async () => {
    if (!whUrl.trim() || whEvents.length === 0) {
      setError('URL et au moins un événement sont requis.');
      return;
    }
    setSavingWh(true);
    setError('');
    try {
      const res = await webhookService.create({
        url: whUrl.trim(),
        events: whEvents,
        description: whDescription.trim() || undefined,
      });
      setWebhooks((prev) => [res.data.webhook, ...prev]);
      setNewWebhookSecret(res.data.webhook.secret);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la création du webhook');
    } finally {
      setSavingWh(false);
    }
  };

  const handleToggleWebhook = async (wh) => {
    try {
      const res = await webhookService.update(wh.id, { is_active: !wh.is_active });
      setWebhooks((prev) => prev.map((w) => (w.id === wh.id ? res.data.webhook : w)));
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteWebhook = async (id) => {
    if (!window.confirm('Supprimer ce webhook ?')) return;
    try {
      await webhookService.remove(id);
      setWebhooks((prev) => prev.filter((w) => w.id !== id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleTestWebhook = async (id) => {
    setTestResult((prev) => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await webhookService.test(id);
      setTestResult((prev) => ({ ...prev, [id]: res.data.result }));
    } catch (e) {
      setTestResult((prev) => ({ ...prev, [id]: { success: false, error: e.response?.data?.error || 'Échec' } }));
    }
  };

  const openDeliveries = async (id) => {
    setDeliveriesDialog(true);
    setLoadingDeliveries(true);
    try {
      const res = await webhookService.deliveries(id);
      setDeliveries(res.data.deliveries);
    } catch (e) {
      setDeliveries([]);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Code /> Portail développeur
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connectez ERA à vos applications (CRM, facturation, automatisations…) via l'API et les webhooks.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ── Les 3 variables de connexion ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Paramètres de connexion</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Voici les valeurs à fournir à vos développeurs pour brancher une application externe sur ERA.
        </Typography>
        <List dense>
          <ListItem
            secondaryAction={<CopyButton value={API_BASE_URL} />}
            sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}
          >
            <ListItemText
              primary="ERA_API_URL"
              secondary={API_BASE_URL}
              secondaryTypographyProps={{ sx: { fontFamily: 'monospace' } }}
            />
          </ListItem>
          <ListItem sx={{ bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
            <ListItemText
              primary="ERA_API_TOKEN"
              secondary="Un token era_… à générer ci-dessous. À envoyer en header : Authorization: Bearer era_…"
            />
          </ListItem>
          <ListItem sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
            <ListItemText
              primary="ERA_WEBHOOK_URL"
              secondary="L'URL de VOTRE application où ERA enverra les événements. Déclarez-la dans la section Webhooks."
            />
          </ListItem>
        </List>
      </Paper>

      {/* ── Tokens API ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKey /> Tokens d'API
          </Typography>
          <Button startIcon={<Add />} variant="contained" size="small" onClick={() => setTokenDialog(true)}>
            Nouveau token
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Chaque token identifie votre compte et ses permissions. Traitez-le comme un mot de passe.
        </Typography>
        {loadingTokens ? (
          <Skeleton variant="rectangular" height={80} />
        ) : tokens.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun token pour l'instant.</Typography>
        ) : (
          <List>
            {tokens.map((token, i) => (
              <React.Fragment key={token.id}>
                {i > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    <Tooltip title="Révoquer">
                      <IconButton edge="end" color="error" onClick={() => handleRevokeToken(token.id)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">{token.name}</Typography>
                        <Chip label={token.token_prefix + '…'} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                      </Box>
                    }
                    secondary={`Créé le ${new Date(token.created_at).toLocaleDateString()}${token.last_used_at ? ` · dernier usage ${new Date(token.last_used_at).toLocaleDateString()}` : ' · jamais utilisé'}`}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* ── Webhooks ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WebhookIcon /> Webhooks
          </Typography>
          <Button startIcon={<Add />} variant="contained" size="small" onClick={openWhDialog}>
            Nouveau webhook
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          ERA enverra un POST signé (header <code>X-ERA-Signature</code>) à vos URLs lorsqu'un événement survient.
        </Typography>
        {loadingWebhooks ? (
          <Skeleton variant="rectangular" height={80} />
        ) : webhooks.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun webhook configuré.</Typography>
        ) : (
          <List>
            {webhooks.map((wh, i) => (
              <React.Fragment key={wh.id}>
                {i > 0 && <Divider component="li" />}
                <ListItem alignItems="flex-start" sx={{ flexWrap: 'wrap' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>{wh.url}</Typography>
                        {!wh.is_active && <Chip label="inactif" size="small" color="warning" />}
                        {wh.failure_count > 0 && <Chip label={`${wh.failure_count} échecs`} size="small" color="error" variant="outlined" />}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {wh.description && <Typography variant="caption" display="block" color="text.secondary">{wh.description}</Typography>}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {wh.events.map((ev) => (
                            <Chip key={ev} label={ev} size="small" sx={{ fontSize: 10, height: 20 }} />
                          ))}
                        </Box>
                        {testResult[wh.id] && !testResult[wh.id].loading && (
                          <Typography variant="caption" color={testResult[wh.id].success ? 'success.main' : 'error.main'} sx={{ display: 'block', mt: 0.5 }}>
                            {testResult[wh.id].success
                              ? `✓ Test réussi (HTTP ${testResult[wh.id].status_code})`
                              : `✗ Test échoué : ${testResult[wh.id].error || 'HTTP ' + testResult[wh.id].status_code}`}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                    <Tooltip title={wh.is_active ? 'Désactiver' : 'Activer'}>
                      <Switch size="small" checked={!!wh.is_active} onChange={() => handleToggleWebhook(wh)} />
                    </Tooltip>
                    <Tooltip title="Envoyer un test">
                      <IconButton size="small" onClick={() => handleTestWebhook(wh.id)}>
                        {testResult[wh.id]?.loading ? <CircularProgress size={18} /> : <Send fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Historique des livraisons">
                      <IconButton size="small" onClick={() => openDeliveries(wh.id)}>
                        <History fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => handleDeleteWebhook(wh.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* ── Documentation ── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Documentation</Typography>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Démarrage rapide (créer un projet)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', fontSize: 13 }}>
{`curl -X POST ${API_BASE_URL}/projects \\
  -H "Authorization: Bearer era_VOTRE_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Projet issu du CRM",
    "description": "Client signé le 15/09",
    "priority": "high"
  }'`}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Format d'un événement webhook</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 1 }}>
              ERA envoie un POST JSON avec ces headers : <code>X-ERA-Event</code>, <code>X-ERA-Delivery</code>,
              <code> X-ERA-Signature</code>, <code>X-ERA-Timestamp</code>. Corps :
            </Typography>
            <Box component="pre" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', fontSize: 13 }}>
{`{
  "id": "uuid-de-la-livraison",
  "event": "project.created",
  "created_at": "2026-09-15T19:00:00.000Z",
  "data": { "id": 42, "title": "Projet issu du CRM", ... }
}`}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Vérifier la signature (sécurité)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Recalculez le HMAC-SHA256 du corps brut avec le secret <code>whsec_…</code> et comparez-le au header <code>X-ERA-Signature</code>.
            </Typography>
            <Box component="pre" sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, overflow: 'auto', fontSize: 13 }}>
{`const crypto = require('crypto');

function verify(rawBody, signatureHeader, secret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signatureHeader),
    Buffer.from(expected)
  );
}`}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>Événements disponibles</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(availableEvents.length ? availableEvents : []).map((ev) => (
                <Chip key={ev} label={ev} size="small" />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* ── Dialog création token ── */}
      <Dialog open={tokenDialog} onClose={closeTokenDialog} fullWidth maxWidth="sm">
        <DialogTitle>Nouveau token d'API</DialogTitle>
        <DialogContent>
          {newTokenValue ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Copiez ce token maintenant : il ne sera plus jamais affiché.
              </Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{newTokenValue}</Typography>
                <CopyButton value={newTokenValue} />
              </Box>
            </Box>
          ) : (
            <TextField
              autoFocus
              fullWidth
              label="Nom du token (ex: Intégration CRM)"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {newTokenValue ? (
            <Button onClick={closeTokenDialog} variant="contained">Terminé</Button>
          ) : (
            <>
              <Button onClick={closeTokenDialog}>Annuler</Button>
              <Button onClick={handleCreateToken} variant="contained" disabled={creatingToken || !tokenName.trim()}>
                {creatingToken ? <CircularProgress size={20} /> : 'Créer'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Dialog création webhook ── */}
      <Dialog open={whDialog} onClose={() => setWhDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nouveau webhook</DialogTitle>
        <DialogContent>
          {newWebhookSecret ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Webhook créé ! Conservez ce secret pour vérifier les signatures :
              </Alert>
              <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{newWebhookSecret}</Typography>
                <CopyButton value={newWebhookSecret} />
              </Box>
            </Box>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                autoFocus
                fullWidth
                label="URL de destination (ERA_WEBHOOK_URL)"
                placeholder="https://votre-app.com/webhooks/era"
                value={whUrl}
                onChange={(e) => setWhUrl(e.target.value)}
              />
              <FormControl fullWidth>
                <InputLabel>Événements</InputLabel>
                <Select
                  multiple
                  value={whEvents}
                  onChange={(e) => setWhEvents(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  input={<OutlinedInput label="Événements" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                    </Box>
                  )}
                >
                  {availableEvents.map((ev) => (
                    <MenuItem key={ev} value={ev}>{ev}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Description (optionnel)"
                value={whDescription}
                onChange={(e) => setWhDescription(e.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {newWebhookSecret ? (
            <Button onClick={() => setWhDialog(false)} variant="contained">Terminé</Button>
          ) : (
            <>
              <Button onClick={() => setWhDialog(false)}>Annuler</Button>
              <Button onClick={handleCreateWebhook} variant="contained" disabled={savingWh || !whUrl.trim() || whEvents.length === 0}>
                {savingWh ? <CircularProgress size={20} /> : 'Créer'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Dialog livraisons ── */}
      <Dialog open={deliveriesDialog} onClose={() => setDeliveriesDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Historique des livraisons</DialogTitle>
        <DialogContent>
          {loadingDeliveries ? (
            <Skeleton variant="rectangular" height={120} />
          ) : deliveries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Aucune livraison enregistrée.</Typography>
          ) : (
            <List dense>
              {deliveries.map((d) => (
                <ListItem key={d.id} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={d.status}
                          size="small"
                          color={d.status === 'success' ? 'success' : 'error'}
                        />
                        <Typography variant="body2">{d.event}</Typography>
                        {d.status_code && <Chip label={`HTTP ${d.status_code}`} size="small" variant="outlined" />}
                      </Box>
                    }
                    secondary={`${new Date(d.created_at).toLocaleString()}${d.error_message ? ' · ' + d.error_message : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeliveriesDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default DeveloperPage;
